import React, {useEffect, useState} from 'react';
import Grid from "@material-ui/core/Grid";
import Plot from "react-plotly.js";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import {makeStyles, Theme, useTheme} from "@material-ui/core/styles";
import Divider from "@material-ui/core/Divider";
import InputRules from "../Model/SingleHCLModelView/SingleTreeView/Inputs/InputRules";
import {AcceptanceLinesContext} from "../Primitives/AcceptanceLinesContext";

const useStyles = makeStyles((theme: Theme) => ({
	fullHeight: {
		height: '100%'
	},
	sidePanel: {
		paddingRight: theme.spacing(4),
		paddingTop: theme.spacing(12),
		maxWidth: "350px"
	},
	dividerRoot: {
		margin: `${theme.spacing(1)}px 0px`
	},
	combinedImpactBG: {
		backgroundColor: theme.palette.background.default
	}
}));

interface Props {
	data: any;
	xAxisTitle: string;
	weights?: boolean;
	withAcceptanceLines?: boolean;
	acceptanceLines: AcceptanceLinesContext;
	type: "tline" | "assets";
}

export enum PlotTypes{
	ACRES = "Acres Burned",
	BUILDING_IMPACTED = "Building Impacted",
	POPULATION_IMPACTED = "Safety (Population Impacted)",
	COMBINED_IMPACT = "Combined Impact"
}
const RiskCurvesPlot = (props: Props) => {
	const { data, xAxisTitle, weights, withAcceptanceLines, acceptanceLines, type } = props;

	const [acceptanceLine, setAcceptanceLine] = useState(acceptanceLines && acceptanceLines.data[type][xAxisTitle] ? acceptanceLines.data[type][xAxisTitle] : null);

	const [acceptanceLinePoint1X, setAcceptanceLinePoint1X] = useState<any>(null);
	const [acceptanceLinePoint1Y, setAcceptanceLinePoint1Y] = useState<any>(null);
	const [acceptanceLinePoint2X, setAcceptanceLinePoint2X] = useState<any>(null);
	const [acceptanceLinePoint2Y, setAcceptanceLinePoint2Y] = useState<any>(null);

	const [plotData, setPlotData] = useState(null);

	const [showWeights, setShowWeights] = useState(false);
	const [wSafety, setWSafety] = useState(0.5);
	const [wBuilding, setWBuilding] = useState(0.3);
	const [wAcres, setWAcres] = useState(0.2);

	const classes = useStyles();
	const theme = useTheme();

	useEffect(() => {
		if (data && withAcceptanceLines && acceptanceLine) {
			const criterion = {
				line: {
					color: "red"
				},
				name: "Acceptance Line",
				type: "scatter",
				x: [acceptanceLine.point1[0], acceptanceLine.point2[0]],
				y: [acceptanceLine.point1[1], acceptanceLine.point2[1]],
			}
			const newData = [...data, criterion];
			setPlotData(newData);
			setAcceptanceLinePoint1X(acceptanceLine.point1[0]);
			setAcceptanceLinePoint1Y(acceptanceLine.point1[1]);
			setAcceptanceLinePoint2X(acceptanceLine.point2[0]);
			setAcceptanceLinePoint2Y(acceptanceLine.point2[1]);
		} else if (data) {
			setPlotData(data)
		}
	},[data, acceptanceLine]);

	const acceptanceLinePoint1XInvalid = InputRules.isEmpty(acceptanceLinePoint1X) || !InputRules.isNumber(acceptanceLinePoint1X);
	const acceptanceLinePoint2XInvalid = InputRules.isEmpty(acceptanceLinePoint2X) || !InputRules.isNumber(acceptanceLinePoint2X);
	const acceptanceLinePoint1YInvalid = !InputRules.isProbabilityValid(acceptanceLinePoint1Y) || !InputRules.isNumber(acceptanceLinePoint1Y) || InputRules.isEmpty(acceptanceLinePoint1Y);
	const acceptanceLinePoint2YInvalid = !InputRules.isProbabilityValid(acceptanceLinePoint2Y) || !InputRules.isNumber(acceptanceLinePoint2Y) || InputRules.isEmpty(acceptanceLinePoint2Y);
	const setBtnIsDisabled = acceptanceLinePoint1XInvalid || acceptanceLinePoint2XInvalid || acceptanceLinePoint1YInvalid || acceptanceLinePoint2YInvalid;

	const font = {
		color: theme.palette.text.primary,
		family: theme.typography.fontFamily,
		size: theme.typography.fontSize,
	};

	const xAxisCommon = {
		autorange: true,
		color: theme.palette.text.secondary,
		gridcolor: theme.palette.text.secondary,
	}

	const yAxisCommon = {
		autorange: true,
		color: theme.palette.text.secondary,
		gridcolor: theme.palette.text.secondary,
		showexponent: 'all',
		exponentformat: 'e',
	}

	const layoutCommon = {
		paper_bgcolor: 'transparent',
		plot_bgcolor: 'transparent',
		...font,
		showlegend: true,
		legend: {
			font: {...font}
		}
	}

	const saveAcceptanceLine = () => {
		let newAcceptanceLines = {...acceptanceLines.data};
		newAcceptanceLines = {
			...newAcceptanceLines,
			[type]: {
				...newAcceptanceLines[type],
				[xAxisTitle]: {
					point1: [acceptanceLinePoint1X, acceptanceLinePoint1Y],
					point2: [acceptanceLinePoint2X, acceptanceLinePoint2Y]
				}
			}
		}
		acceptanceLines.setCurrentAcceptanceLines(newAcceptanceLines);
		setAcceptanceLine(newAcceptanceLines[type][xAxisTitle]);
	}

	const isCombinedImpact = props.xAxisTitle === "Combined Impact";

	return (
		<Card>
			<Grid container className={`${classes.fullHeight} ${isCombinedImpact ? classes.combinedImpactBG : undefined}`}>
				<Grid item xs={8}>
					<Plot
						data={plotData ? plotData : data}
						layout={{
							...layoutCommon,
							xaxis: {
								title: xAxisTitle,
								type: 'linear',
								...xAxisCommon
							},
							yaxis: {
								title: 'Probability of Exceedance',
								type: 'linear',
								...yAxisCommon
							},
						}}
					/>
				</Grid>
				<Grid item xs={4} className={classes.sidePanel}>
					{ withAcceptanceLines && (
						<React.Fragment>
							<Typography>Acceptance Line</Typography>
							<Grid container>
								<Grid item xs={6}>
									<Typography variant={"caption"}>Point 1</Typography>
									<TextField
										label="X"
										variant="outlined"
										type={"number"}
										value={acceptanceLinePoint1X}
										min={0}
										error={acceptanceLinePoint1X && acceptanceLinePoint1XInvalid}
										helperText={acceptanceLinePoint1X && acceptanceLinePoint1XInvalid ? "X must be a positive number" : undefined}
										onChange={(e: any) => setAcceptanceLinePoint1X(e.target.value)}
									/>
								</Grid>
								<Grid item xs={6}>
									<Typography variant={"caption"}>Point 2</Typography>
									<TextField
										label="X"
										variant="outlined"
										type={"number"}
										value={acceptanceLinePoint2X}
										min={0}
										error={acceptanceLinePoint2X && acceptanceLinePoint2XInvalid}
										helperText={acceptanceLinePoint2X && acceptanceLinePoint2XInvalid ? "X must be a positive number" : undefined}
										onChange={(e: any) => setAcceptanceLinePoint2X(e.target.value)}
									/>
								</Grid>
								<Grid item xs={6}>
									<TextField
										label="Y"
										variant="outlined"
										type={"number"}
										value={acceptanceLinePoint1Y}
										min={0}
										error={acceptanceLinePoint1Y && acceptanceLinePoint1YInvalid}
										helperText={acceptanceLinePoint1Y && acceptanceLinePoint1YInvalid ? InputRules.DESCRIPTIONS.PROBABILITY : undefined}
										onChange={(e: any) => setAcceptanceLinePoint1Y(e.target.value)}
									/>
								</Grid>
								<Grid item xs={6}>
									<TextField
										label="Y"
										variant="outlined"
										type={"number"}
										value={acceptanceLinePoint2Y}
										min={0}
										error={acceptanceLinePoint2Y && acceptanceLinePoint2YInvalid}
										helperText={acceptanceLinePoint2Y && acceptanceLinePoint2YInvalid ? InputRules.DESCRIPTIONS.PROBABILITY : undefined}
										onChange={(e: any) => setAcceptanceLinePoint2Y(e.target.value)}
									/>
								</Grid>
								<Grid item xs={12}>
									<Button
										fullWidth
										variant={"outlined"}
										color={"primary"}
										disabled={setBtnIsDisabled}
										onClick={saveAcceptanceLine}
									>
										Set
									</Button>
								</Grid>
							</Grid>
						</React.Fragment>
					)}
					{
						weights && (
							<React.Fragment>
								<Divider className={classes.dividerRoot} />
								<Button
									color={"primary"}
									variant={"outlined"}
									fullWidth
									onClick={() => setShowWeights(!showWeights)}
								>
									Weights
								</Button>
								{showWeights &&
								<Grid container direction={"column"}>
									<TextField
										label="Wsafety"
										variant="outlined"
										type={"number"}
										value={wSafety}
										onChange={(e: any) => setWSafety(e.target.value)}
									/>
									<TextField
										label="Wbuilding"
										variant="outlined"
										type={"number"}
										value={wBuilding}
										onChange={(e: any) => setWBuilding(e.target.value)}
									/>
									<TextField
										label="Wacres"
										variant="outlined"
										type={"number"}
										value={wAcres}
										onChange={(e: any) => setWAcres(e.target.value)}
									/>
								</Grid>
								}
							</React.Fragment>
						)
					}
				</Grid>
			</Grid>
		</Card>
	);
};

export default RiskCurvesPlot;